import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { delay, map, mergeMap, Observable, of, retry, Subject, tap } from 'rxjs';


import { AuthService } from './auth.service';

import { environment } from '@env';
import { AnnualReport } from '@/annual-report/models';
import { PollingStatus } from '@/tax/models';
import { AnnualReportPrompt } from '@/annual-report/models/annual-report-prompt.model';

export type AnnualReportProgressModel = { progress: number };

type WebSocketParams = {
    webSocket?: WebSocket;
    organisationId?: number;
    conversationId?: number;
    isConnected: boolean;
};

const LONG_DELAY_STATUS_FETCH_MS = 5000;
const invalidErrorMessage = 'Not ready';

@Injectable({
    providedIn: 'root',
})
export class AnnualReportService {
    constructor(
        private http: HttpClient,
        private authService: AuthService,
    ) {
    }

    listAnnualReport(orgId: number): Observable<AnnualReport[]> {
        return this.http
            .get<AnnualReport[]>(environment.endpoints.organisationAnnualReportList(orgId))
            .pipe(map((reports) => reports.map((r) => new AnnualReport().deserialize(r))));
    }

    getAnnualReport(orgId: number, reportId: number): Observable<AnnualReport> {
        return this.http
            .get<AnnualReport>(environment.endpoints.organisationAnnualReportDetails(orgId, reportId))
            .pipe(map((rep) => new AnnualReport().deserialize(rep)));
    }

    createAnnualReport(orgId: number, periodId: number, companyId: number, firmId: number, promptId?: number, comparisonPeriodId?: number): Observable<AnnualReport> {
        return this.http
            .post<AnnualReport>(environment.endpoints.organisationAnnualReportList(orgId), {
                'period_pk': periodId,
                'company_pk': companyId,
                'firm_pk': firmId,
                'prompt_id': promptId,
                'comparison_period_id': comparisonPeriodId,
            })
            .pipe(map((r) => new AnnualReport().deserialize(r)));
    }

    createAnnualReportAndPollResult(orgId: number, periodId: number, companyId: number, firmId: number, progress?: AnnualReportProgressModel, promptId?: number, comparisonPeriodId?: number): Observable<AnnualReport> {
        return this.createAnnualReport(orgId, periodId, companyId, firmId, promptId, comparisonPeriodId).pipe(
            delay(LONG_DELAY_STATUS_FETCH_MS),
            mergeMap((report: AnnualReport) =>
                this.getAnnualReport(orgId, report.id).pipe(
                    tap((s) => {
                        if (progress) {
                            const serverProgress = s.progress ?? 0;
                            const current = progress.progress ?? 0;
                            // Never let the displayed progress go backwards during polling
                            progress.progress = Math.max(current, serverProgress);
                        }
                        if (s.status === PollingStatus.Init || s.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                        if (s.status === PollingStatus.Fail) {
                            throw Error('Annual report generation failed.');
                        }

                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(LONG_DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    getAnnualReportPrompt(orgId: number, promptId: number): Observable<AnnualReportPrompt> {
        return this.http
            .get<AnnualReportPrompt>(environment.endpoints.organisationAnnualReportPromptDetails(orgId, promptId))
            .pipe(map((rep) => new AnnualReportPrompt().deserialize(rep)));
    }

    listAnnualReportPrompt(orgId: number): Observable<AnnualReportPrompt[]> {
        return this.http
            .get<AnnualReportPrompt[]>(environment.endpoints.organisationAnnualReportPromptList(orgId))
            .pipe(map((prompts) => prompts.map((r) => new AnnualReportPrompt().deserialize(r))));
    }

    createAnnualReportPrompt(orgId: number, title: string, promptText: string): Observable<AnnualReportPrompt> {
        return this.http
            .post<AnnualReportPrompt>(environment.endpoints.organisationAnnualReportPromptList(orgId), {'title': title, 'prompt': promptText})
            .pipe(map((r) => new AnnualReportPrompt().deserialize(r)));
    }

    updateAnnualReportPrompt(orgId: number, id: number, title: string, promptText: string) {
        return this.http
            .put<AnnualReportPrompt>(
                environment.endpoints.organisationAnnualReportPromptDetails(orgId, id),
                {title: title, prompt: promptText},
            )
            .pipe(map((r) => new AnnualReportPrompt().deserialize(r)));
    }

    deleteAnnualReportPrompt(orgId: number, id: number) {
        return this.http.delete<void>(environment.endpoints.organisationAnnualReportPromptDetails(orgId, id));
    }
}
